"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ApplicationBuilder = void 0;
const inversify_1 = require("inversify");
const application_1 = require("../application/application");
/**
 * Default builder to create and configure a new {@link Application} instance.
 */
class ApplicationBuilder {
    /** @internal */
    constructor(logger) {
        /** @internal */ this.lazyActions = [];
        this.logger = logger;
        this.config = {
            rootContainer: new inversify_1.Container(),
            plugins: new Map(),
            modules: new Map(),
        };
        this.prependDefaultPlugin();
    }
    /**
     * Create a new builder with the specified logger.
     * @param logger - A logger instance
     */
    static create(logger) {
        return new ApplicationBuilder(logger);
    }
    /**
     * Set the DI container of the application.
     *
     * @remarks
     * If not set, a default container will be created.
     *
     * @param container - A DI container instance
     */
    container(container) {
        this.config.rootContainer = container;
        return this;
    }
    /**
     * Override this method to install default plugins before those specified by the user.
     */
    prependDefaultPlugin() {
        // Do nothing by default, can be overriden
    }
    /**
     * Override this method to install default plugins after those specified by the user.
     */
    appendDefaultPlugins() {
        // Do nothing by default, can be overriden
    }
    /**
     * Apply the pending lazy actions
     */
    applyLazyActions() {
        for (const action of this.lazyActions)
            action();
    }
    /**
     * Add a new plugin to the application.
     */
    plugin(...args) {
        const [pluginConstructor, config] = args;
        if (this.config.plugins.has(pluginConstructor)) {
            throw new Error(`Plugin ${pluginConstructor.name} is already registered.`);
        }
        this.config.plugins.set(pluginConstructor, config);
        return this;
    }
    /**
     * Configure a plugin added to the application.
     * @param pluginConstructor - The plugin constructor
     * @param configurator - A function that returns a new configuration for this plugin
     */
    configure(pluginConstructor, configurator) {
        this.lazyActions.push(() => {
            if (!this.config.plugins.has(pluginConstructor)) {
                throw new Error(`Plugin ${pluginConstructor.name} is not registered.`);
            }
            const currentConfig = this.config.plugins.get(pluginConstructor);
            this.config.plugins.set(pluginConstructor, configurator(currentConfig));
        });
        return this;
    }
    /**
     * Remove a plugin added to the application.
     * @param pluginConstructor - The plugin constructor
     */
    eject(pluginConstructor) {
        this.lazyActions.push(() => {
            this.config.plugins.delete(pluginConstructor);
        });
        return this;
    }
    /**
     * Add a new module to the application.
     */
    module(...args) {
        const [moduleConstructor, config] = args;
        if (this.config.modules.has(moduleConstructor)) {
            throw new Error(`Module ${moduleConstructor.name} is already registered.`);
        }
        this.config.modules.set(moduleConstructor, config);
        return this;
    }
    /**
     * Configure and start the application.
     */
    async start() {
        this.appendDefaultPlugins();
        this.applyLazyActions();
        const app = new application_1.Application(this.logger, this.config);
        return app.start();
    }
}
exports.ApplicationBuilder = ApplicationBuilder;
//# sourceMappingURL=builder.js.map